import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Play, Briefcase, Coins, BarChart3, MoreVertical, Pencil, Copy, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SimulationResponse } from '@/types/simulation.ts';
import { maestros, getMaestroName } from '@/types/maestro.ts';
import { useCatalogLabel } from '@/hooks/useCatalog.ts';
import DocumentsStatusBadge from '@/components/simulation/DocumentsStatusBadge.tsx';

interface SimulationCardProps {
  simulation: SimulationResponse;
  showActions?: boolean;
  onEdit?: (simulation: SimulationResponse) => void;
  onDuplicate?: (simulation: SimulationResponse) => void;
  onDelete?: (simulation: SimulationResponse) => void;
}

export default function SimulationCard({
  simulation,
  showActions,
  onEdit,
  onDuplicate,
  onDelete,
}: SimulationCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const typeLabel = useCatalogLabel('SimulationType', simulation.simulationType);
  const diffLabel = useCatalogLabel('Difficulty', String(simulation.difficultyLevel));
  const phaseLabel = useCatalogLabel('SalePhase', simulation.salePhase);
  const sectorLabel = useCatalogLabel('Sector', simulation.sector);
  const subSectorLabel = useCatalogLabel('SubSector', simulation.subSector);
  const priceLabel = useCatalogLabel('PriceRange', simulation.priceRange);

  const maestro = simulation.assignedMaestroId
    ? maestros.find((x) => x.id === simulation.assignedMaestroId)
    : null;

  return (
    <div className="group relative flex w-full flex-col rounded-xl border border-border bg-card p-5 text-left transition-all hover:border-primary/30 hover:shadow-md">
      {/* Main clickable area */}
      <button
        onClick={() => navigate(`/simulation/${simulation.id}`)}
        className="flex w-full flex-1 flex-col text-left"
      >
        {/* Header: name only */}
        <div className="mb-1.5 pr-6">
          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
            {simulation.name}
          </h3>
        </div>

        {/* Type › Phase — primary subtitle (linked) */}
        <p className="mb-4 text-sm text-muted-foreground">
          <span className="font-medium text-foreground/80">{typeLabel}</span>
          <span className="text-muted-foreground/60"> › {phaseLabel}</span>
        </p>

        {/* Context chips — line 1: sector › sub-sector */}
        {simulation.sector && (
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              <Briefcase className="size-3.5" />
              <span>
                {sectorLabel}
                {simulation.subSector && <span className="text-muted-foreground/60"> › {subSectorLabel}</span>}
              </span>
            </span>
          </div>
        )}

        {/* Context chips — line 2: maestro · price */}
        <div className="mb-4 flex flex-wrap gap-1.5">
          {maestro ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ backgroundColor: `${maestro.accentColor}15`, color: maestro.accentColor }}
            >
              <span>{maestro.icon}</span>
              <span>{getMaestroName(maestro)}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              <span>🎯</span>
              <span>{t('simulation.maestroToChoose')}</span>
            </span>
          )}

          {simulation.priceRange && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              <Coins className="size-3.5" />
              <span>{priceLabel}</span>
            </span>
          )}
        </div>

        {/* Footer meta row — fixed slot: difficulty · documents · CTA */}
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5" title={diffLabel}>
            <BarChart3 className="size-3.5" />
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={`size-1.5 rounded-full ${
                    level <= simulation.difficultyLevel ? 'bg-primary' : 'bg-border'
                  }`}
                />
              ))}
            </div>
            <span>{diffLabel}</span>
          </div>

          <DocumentsStatusBadge simulation={simulation} showWhenEmpty />

          <div className="ml-auto flex items-center gap-1 text-primary opacity-0 transition-opacity group-hover:opacity-100">
            <Play className="size-3.5" />
            <span className="font-semibold">{t('common.practice')}</span>
          </div>
        </div>
      </button>

      {/* Actions menu — DropdownMenu replaces custom dropdown */}
      {showActions && (
        <div className="absolute right-3 top-3 z-10">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                className="text-muted-foreground opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                onClick={(e) => e.stopPropagation()}
                aria-label={t('common.openMenu')}
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit?.(simulation)}>
                <Pencil className="size-3.5" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate?.(simulation)}>
                <Copy className="size-3.5" />
                {t('simulation.duplicateAction')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDelete?.(simulation)} className="text-destructive focus:text-destructive">
                <Trash2 className="size-3.5" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
